import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getPlayerHistory } from '../../services/chessToolsService';

const PlayerHistoryModal = ({ player, onClose }) => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchHistory = async () => {
      if (!player.id) {
        setError('Player ID not available');
        setLoading(false);
        return;
      }

      try {
        const data = await getPlayerHistory(player.id);
        setHistory(data);
      } catch (err) {
        setError('Failed to load player history');
        console.error('Error fetching player history:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [player]);

  // Get title color (same as PlayerRow)
  const getTitleColor = (title) => {
    switch(title) {
      case 'GM': return 'text-yellow-400';
      case 'IM': return 'text-orange-400';
      case 'FM': return 'text-blue-400';
      case 'WGM': return 'text-yellow-400';
      case 'WIM': return 'text-orange-400';
      case 'WFM': return 'text-blue-400';
      case 'CM': return 'text-green-400';
      case 'WCM': return 'text-green-400';
      default: return 'text-gray-400';
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-xl"
        >
          {/* Header */}
          <div className="bg-gray-900 px-6 py-4 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-100 flex items-center gap-2">
                  <span className={getTitleColor(player.title)}>{player.title}</span>
                  {player.name}
                </h2>
                <p className="text-sm text-gray-400">
                  {player.country} • Rating: {player.rating}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-100 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            {loading ? (
              <div className="text-center py-8">
                <div className="text-gray-400">Loading player history...</div>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <div className="text-red-400">{error}</div>
                <div className="text-sm text-gray-400 mt-2">
                  Rating history may not be available for this player
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Player Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-700 rounded-lg p-4">
                    <div className="text-sm text-gray-400">Current Rating</div>
                    <div className="text-2xl font-bold text-gray-100">{player.rating}</div>
                  </div>
                  <div className="bg-gray-700 rounded-lg p-4">
                    <div className="text-sm text-gray-400">World Rank</div>
                    <div className="text-2xl font-bold text-gray-100">#{player.rank || '-'}</div>
                  </div>
                  <div className="bg-gray-700 rounded-lg p-4">
                    <div className="text-sm text-gray-400">Games Played</div>
                    <div className="text-2xl font-bold text-gray-100">{player.games || '-'}</div>
                  </div>
                  <div className="bg-gray-700 rounded-lg p-4">
                    <div className="text-sm text-gray-400">Age</div>
                    <div className="text-2xl font-bold text-gray-100">{player.age || '-'}</div>
                  </div>
                </div>

                {/* Rating History */}
                {history && history.length > 0 ? (
                  <div>
                    <h3 className="text-lg font-medium text-gray-100 mb-4">Rating History</h3>
                    <div className="bg-gray-700 rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-gray-800">
                          <tr>
                            <th className="px-4 py-2 text-left text-sm text-gray-400">Date</th>
                            <th className="px-4 py-2 text-center text-sm text-gray-400">Rating</th>
                            <th className="px-4 py-2 text-center text-sm text-gray-400">Change</th>
                            <th className="px-4 py-2 text-center text-sm text-gray-400">Games</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-600">
                          {history.map((entry, index) => (
                            <tr key={index}>
                              <td className="px-4 py-2 text-sm text-gray-300">
                                {new Date(entry.date).toLocaleDateString()}
                              </td>
                              <td className="px-4 py-2 text-sm text-center font-medium text-gray-100">
                                {entry.rating}
                              </td>
                              <td className="px-4 py-2 text-sm text-center">
                                <span className={entry.change > 0 ? 'text-green-400' : entry.change < 0 ? 'text-red-400' : 'text-gray-400'}>
                                  {entry.change > 0 && '+'}
                                  {entry.change || '0'}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-sm text-center text-gray-400">
                                {entry.games || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    No historical data available
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default PlayerHistoryModal;